import { useEffect, useMemo, useState } from "react";
import api from "../api/api";

import {
    PageHeader,
    Panel,
    InfoRow,
    KpiCard,
} from "../components/ui/AdminUI";

import DataTable from "../components/ui/DataTable";
import StatusBadge from "../components/ui/StatusBadge";

import {
    TextInput,
    NumberInput,
    PrimaryButton,
    SuccessButton,
    DangerButton,
    SecondaryButton,
} from "../components/ui/FormControls";

const EMPTY_ZONE = {
    name: "",
    centerLat: "",
    centerLng: "",
    radiusMeters: "",
};

const EMPTY_CHECK = {
    lat: "",
    lng: "",
};

export default function GeoFencePage() {
    const [zones, setZones] = useState([]);
    const [form, setForm] = useState(EMPTY_ZONE);
    const [check, setCheck] = useState(EMPTY_CHECK);
    const [result, setResult] = useState(null);

    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [checking, setChecking] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const loadZones = async () => {
        try {
            setLoading(true);
            setError("");

            const res = await api.get("/GeoFence");
            setZones(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to load geofence zones."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadZones();
    }, []);

    const createZone = async (e) => {
        e.preventDefault();

        setError("");
        setSuccess("");
        setResult(null);

        const centerLat = Number(form.centerLat);
        const centerLng = Number(form.centerLng);
        const radiusMeters = Number(form.radiusMeters);

        if (!form.name.trim()) {
            setError("Zone name is required.");
            return;
        }

        if (!isValidLatitude(centerLat)) {
            setError("Latitude must be between -90 and 90.");
            return;
        }

        if (!isValidLongitude(centerLng)) {
            setError("Longitude must be between -180 and 180.");
            return;
        }

        if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) {
            setError("Radius must be greater than 0.");
            return;
        }

        try {
            setCreating(true);

            await api.post("/GeoFence", {
                name: form.name.trim(),
                centerLat,
                centerLng,
                radiusMeters,
            });

            setForm(EMPTY_ZONE);
            setSuccess("GeoFence zone created successfully.");

            await loadZones();
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to create zone."
            );
        } finally {
            setCreating(false);
        }
    };

    const deleteZone = async (id) => {
        if (!window.confirm("Delete this GeoFence zone?")) return;

        try {
            setDeletingId(id);
            setError("");
            setSuccess("");

            await api.delete(`/GeoFence/${id}`);

            setZones((prev) => prev.filter((zone) => zone.id !== id));
            setSuccess("GeoFence zone deleted successfully.");
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to delete zone."
            );
        } finally {
            setDeletingId(null);
        }
    };

    const checkPoint = async (e) => {
        e.preventDefault();

        setError("");
        setSuccess("");
        setResult(null);

        const lat = Number(check.lat);
        const lng = Number(check.lng);

        if (!isValidLatitude(lat)) {
            setError("Check latitude must be between -90 and 90.");
            return;
        }

        if (!isValidLongitude(lng)) {
            setError("Check longitude must be between -180 and 180.");
            return;
        }

        try {
            setChecking(true);

            const res = await api.get("/GeoFence/check", {
                params: { lat, lng },
            });

            setResult(res.data);
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to check location."
            );
        } finally {
            setChecking(false);
        }
    };

    const stats = useMemo(() => {
        const totalRadius = zones.reduce(
            (sum, zone) => sum + Number(zone.radiusMeters || 0),
            0
        );

        const averageRadius =
            zones.length > 0 ? Math.round(totalRadius / zones.length) : 0;

        return {
            total: zones.length,
            totalRadius,
            averageRadius,
        };
    }, [zones]);

    const columns = [
        {
            key: "name",
            label: "Zone",
            render: (row) => (
                <span className="font-bold text-slate-900">
                    {row.name || "�"}
                </span>
            ),
        },
        {
            key: "centerLat",
            label: "Latitude",
            render: (row) => formatCoordinate(row.centerLat),
        },
        {
            key: "centerLng",
            label: "Longitude",
            render: (row) => formatCoordinate(row.centerLng),
        },
        {
            key: "radiusMeters",
            label: "Radius",
            render: (row) =>
                Number.isFinite(Number(row.radiusMeters))
                    ? `${Number(row.radiusMeters).toLocaleString()} m`
                    : "�",
        },
        {
            key: "status",
            label: "Status",
            render: () => <StatusBadge status="Active" />,
        },
        {
            key: "actions",
            label: "Actions",
            render: (row) => (
                <DangerButton
                    className="px-3 py-2"
                    disabled={deletingId === row.id}
                    onClick={() => deleteZone(row.id)}
                >
                    {deletingId === row.id ? "Deleting..." : "Delete"}
                </DangerButton>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="GeoFence Management"
                subtitle="Create logistics zones, verify shipment coordinates and monitor geographic boundaries."
                action={
                    <SecondaryButton onClick={loadZones} disabled={loading}>
                        {loading ? "Refreshing..." : "Refresh Zones"}
                    </SecondaryButton>
                }
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            {success && (
                <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-green-700">
                    {success}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <KpiCard
                    title="Total Zones"
                    value={stats.total}
                    color="blue"
                />

                <KpiCard
                    title="Average Radius"
                    value={`${stats.averageRadius.toLocaleString()} m`}
                    color="purple"
                />

                <KpiCard
                    title="Last Check"
                    value={
                        result
                            ? result.inside
                                ? "Inside"
                                : "Outside"
                            : "Not checked"
                    }
                    color={
                        result
                            ? result.inside
                                ? "green"
                                : "yellow"
                            : "slate"
                    }
                />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Panel
                    title="Create Zone"
                    subtitle="Define the center point and radius of a new GeoFence."
                >
                    <form onSubmit={createZone} className="space-y-4">
                        <TextInput
                            label="Zone Name"
                            value={form.name}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                }))
                            }
                            placeholder="Zone name"
                            required
                        />

                        <div className="grid md:grid-cols-2 gap-4">
                            <NumberInput
                                label="Center Latitude"
                                value={form.centerLat}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        centerLat: e.target.value,
                                    }))
                                }
                                placeholder="56.9496"
                                step="any"
                                min="-90"
                                max="90"
                                required
                            />

                            <NumberInput
                                label="Center Longitude"
                                value={form.centerLng}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        centerLng: e.target.value,
                                    }))
                                }
                                placeholder="24.1052"
                                step="any"
                                min="-180"
                                max="180"
                                required
                            />
                        </div>

                        <NumberInput
                            label="Radius in Metres"
                            value={form.radiusMeters}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    radiusMeters: e.target.value,
                                }))
                            }
                            placeholder="1000"
                            min="1"
                            required
                        />

                        <PrimaryButton
                            type="submit"
                            disabled={creating}
                        >
                            {creating ? "Creating..." : "Create Zone"}
                        </PrimaryButton>
                    </form>
                </Panel>

                <Panel
                    title="Check Location"
                    subtitle="Verify whether a coordinate is inside one of the active zones."
                >
                    <form onSubmit={checkPoint} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <NumberInput
                                label="Latitude"
                                value={check.lat}
                                onChange={(e) =>
                                    setCheck((prev) => ({
                                        ...prev,
                                        lat: e.target.value,
                                    }))
                                }
                                placeholder="56.9496"
                                step="any"
                                min="-90"
                                max="90"
                                required
                            />

                            <NumberInput
                                label="Longitude"
                                value={check.lng}
                                onChange={(e) =>
                                    setCheck((prev) => ({
                                        ...prev,
                                        lng: e.target.value,
                                    }))
                                }
                                placeholder="24.1052"
                                step="any"
                                min="-180"
                                max="180"
                                required
                            />
                        </div>

                        <SuccessButton
                            type="submit"
                            disabled={checking}
                        >
                            {checking ? "Checking..." : "Check Location"}
                        </SuccessButton>
                    </form>

                    {result && (
                        <div
                            className={`mt-5 rounded-2xl border p-5 ${result.inside
                                    ? "border-green-200 bg-green-50"
                                    : "border-yellow-200 bg-yellow-50"
                                }`}
                        >
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                                        Location Result
                                    </p>

                                    <p
                                        className={`mt-1 text-2xl font-black ${result.inside
                                                ? "text-green-800"
                                                : "text-yellow-800"
                                            }`}
                                    >
                                        {result.inside
                                            ? "Inside GeoFence"
                                            : "Outside all zones"}
                                    </p>
                                </div>

                                <StatusBadge
                                    status={
                                        result.inside
                                            ? "Inside"
                                            : "Outside"
                                    }
                                />
                            </div>

                            {result.inside && (
                                <div className="mt-4 grid md:grid-cols-2 gap-4">
                                    <InfoRow
                                        label="Zone"
                                        value={
                                            result.zone ||
                                            result.zoneName ||
                                            "�"
                                        }
                                    />

                                    <InfoRow
                                        label="Zone ID"
                                        value={result.zoneId ?? "�"}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Panel>
            </div>

            <Panel
                title="GeoFence Zones"
                subtitle="Registered logistics zones available to the platform."
            >
                {loading ? (
                    <p className="text-slate-500">
                        Loading GeoFence zones...
                    </p>
                ) : (
                    <DataTable
                        columns={columns}
                        rows={zones}
                        rowKey="id"
                        emptyText="No GeoFence zones found."
                    />
                )}
            </Panel>
        </div>
    );
}

function isValidLatitude(value) {
    return Number.isFinite(value) && value >= -90 && value <= 90;
}

function isValidLongitude(value) {
    return Number.isFinite(value) && value >= -180 && value <= 180;
}

function formatCoordinate(value) {
    const number = Number(value);

    if (!Number.isFinite(number)) return "�";

    return number.toFixed(6);
}